import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { 
  Package, ShoppingCart, Store as StoreIcon, TrendingUp, Check, Crown, 
  QrCode, Download, Copy, Plus, Eye, ArrowRight, Sparkles 
} from 'lucide-react';
import { getStoreByOwner, getProductsByStore, getOrdersByStore, getAllPlans } from '../lib/firebaseService';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'sonner';

export default function Dashboard() {
  const { firebaseUser } = useAuth();
  const [stats, setStats] = useState({
    products: 0,
    orders: 0,
    pendingOrders: 0
  });
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    if (firebaseUser) {
      loadData();
    }
  }, [firebaseUser]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [userStore, plansData] = await Promise.all([
        getStoreByOwner(firebaseUser.uid),
        getAllPlans()
      ]);
      
      setStore(userStore);
      setPlans(plansData);
      
      if (userStore) {
        const [products, orders] = await Promise.all([
          getProductsByStore(userStore.id),
          getOrdersByStore(userStore.id)
        ]);
        
        setStats({
          products: products.length,
          orders: orders.length,
          pendingOrders: orders.filter(o => o.status === 'pending').length
        });
      }
    } catch (error) {
      console.error('Məlumatlar yüklənə bilmədi:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyStoreLink = () => {
    const link = `${window.location.origin}/store/${store.id}`;
    navigator.clipboard.writeText(link);
    toast.success('Link kopyalandı!');
  };

  const storeLink = store ? `${window.location.origin}/store/${store.id}` : '';

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-pulse text-gray-400">Yüklənir...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-home">
        
        {/* Welcome Header */}
        <div className="bg-gradient-to-r from-black to-gray-800 rounded-2xl p-6 text-white">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">
            Xoş gəldiniz! 👋
          </h1>
          <p className="text-gray-300 text-sm md:text-base">
            {store ? 'Mağazanızın vəziyyətinə baxın' : 'Öz online mağazanızı yaradın'}
          </p>
        </div>

        {/* No Store - Create Store CTA */}
        {!store && (
          <div className="bg-white rounded-2xl border-2 border-dashed border-gray-300 p-8 text-center" data-testid="create-store-banner">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <StoreIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold mb-2">Mağazanız yoxdur</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              İlk addım olaraq mağazanızın adını və WhatsApp nömrənizi daxil edin. 
              Sonra məhsullarınızı əlavə edə bilərsiniz.
            </p>
            <Link
              to="/dashboard/store/info"
              className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-xl font-medium hover:bg-gray-800 transition-all"
              data-testid="create-store-button"
            >
              <Plus className="w-5 h-5" />
              Mağaza Yarat
            </Link>
          </div>
        )}

        {/* Has Store - Show Stats & Quick Actions */}
        {store && (
          <>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-2xl p-5 border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-3">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900">{stats.products}</div>
                <div className="text-sm text-gray-500">Məhsul</div>
              </div>
              
              <div className="bg-white rounded-2xl p-5 border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mb-3">
                  <ShoppingCart className="w-6 h-6 text-green-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900">{stats.orders}</div>
                <div className="text-sm text-gray-500">Sifariş</div>
              </div>
              
              <div className="bg-white rounded-2xl p-5 border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-3">
                  <TrendingUp className="w-6 h-6 text-orange-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900">{stats.pendingOrders}</div>
                <div className="text-sm text-gray-500">Gözləyən</div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Store Link Card */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  🔗 Mağaza Linkiniz
                </h3>
                <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2 mb-4">
                  <input 
                    type="text" 
                    value={storeLink} 
                    readOnly 
                    className="flex-1 bg-transparent text-sm text-gray-600 outline-none truncate"
                  />
                  <button
                    onClick={copyStoreLink}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Kopyala"
                  >
                    <Copy className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
                <div className="flex gap-2">
                  <a
                    href={storeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 bg-black text-white py-2.5 rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    Mağazaya Bax
                  </a>
                  <button
                    onClick={copyStoreLink}
                    className="flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                    Kopyala
                  </button>
                </div>
              </div>

              {/* Add Product CTA */}
              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-6 text-white">
                <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Məhsul Əlavə Et
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  Yeni məhsul əlavə edərək mağazanızı genişləndirin
                </p>
                <Link
                  to="/dashboard/products"
                  className="inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-100 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Məhsul Əlavə Et
                </Link>
              </div>
            </div>

            {/* Store Info Summary */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100">
              <h3 className="font-bold text-lg mb-4">📋 Mağaza Məlumatları</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-gray-500">Mağaza adı</span>
                  <span className="font-medium">{store.store_name}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-gray-500">WhatsApp</span>
                  <span className="font-medium">{store.whatsapp_number || store.phone_number || '-'}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-gray-500">Tip</span>
                  <span className="font-medium">{store.store_type === 'restaurant' ? '🍽️ Restoran' : '🛍️ Mağaza'}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-gray-500">Status</span>
                  <span className={`font-medium ${store.is_active !== false ? 'text-green-600' : 'text-red-600'}`}>
                    {store.is_active !== false ? '✅ Aktiv' : '❌ Deaktiv'}
                  </span>
                </div>
              </div>
              <Link
                to="/dashboard/store/info"
                className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-black mt-4 transition-colors"
              >
                Redaktə et <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* QR Code for Restaurant */}
            {store.store_type === 'restaurant' && (
              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <div className="flex items-start gap-6">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                      <QrCode className="w-5 h-5" />
                      Menyu QR Kodu
                    </h3>
                    <p className="text-gray-500 text-sm mb-4">
                      Bu QR kodu çap edib masalara yapışdırın. Müştərilər skan edərək menyuya baxa bilərlər.
                    </p>
                    <button
                      onClick={() => {
                        const canvas = document.querySelector('canvas');
                        const url = canvas.toDataURL('image/png');
                        const link = document.createElement('a');
                        link.download = `${store.store_name}-qr-kod.png`;
                        link.href = url;
                        link.click();
                      }}
                      className="inline-flex items-center gap-2 bg-black text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      QR Kodu Yüklə
                    </button>
                  </div>
                  <div className="bg-white p-3 rounded-xl border border-gray-200">
                    <QRCodeCanvas
                      value={storeLink}
                      size={120}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Subscription Plans */}
        {plans.length > 0 && (
          <div data-testid="subscription-plans-section">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Crown className="w-6 h-6 text-yellow-500" />
              Planlar
            </h2>
            
            <div className="grid md:grid-cols-3 gap-4">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`bg-white rounded-2xl p-6 border-2 transition-all hover:shadow-lg ${
                    plan.popular ? 'border-black' : 'border-gray-100'
                  }`}
                  data-testid={`dashboard-plan-${plan.id}`}
                >
                  {plan.popular && (
                    <div className="mb-3">
                      <span className="bg-black text-white text-xs px-3 py-1 rounded-full font-medium">
                        ⭐ Populyar
                      </span>
                    </div>
                  )}
                  
                  <h3 className="text-lg font-bold mb-1">{plan.name}</h3>
                  <div className="text-2xl font-bold mb-4">
                    {plan.price_monthly === 0 ? 'Pulsuz' : `${plan.price_monthly} AZN/ay`}
                  </div>

                  <div className="space-y-2 mb-6">
                    {Array.isArray(plan.features) && plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {plan.purchase_link ? (
                    <a
                      href={plan.purchase_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`block w-full text-center py-2.5 rounded-xl font-medium transition-colors ${
                        plan.popular 
                          ? 'bg-black text-white hover:bg-gray-800' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      data-testid={`buy-plan-${plan.id}`}
                    >
                      {plan.price_monthly === 0 ? 'Hazırkı Plan' : 'Seç'}
                    </a>
                  ) : (
                    <button
                      className="w-full py-2.5 rounded-xl font-medium bg-gray-100 text-gray-400 cursor-not-allowed"
                      disabled
                    >
                      {plan.price_monthly === 0 ? 'Hazırkı Plan' : 'Tezliklə'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
